import { useCallback, useEffect, useState } from "react";
import {
  Banner,
  Button,
  Dialog,
  Field,
  Panel,
  Segmented,
  SimpleTable,
  StatGrid,
  StatTile,
  Switch,
  Tag,
  TextArea,
  TextInput,
  useCkToast,
} from "@NP-Webber/crm-kit/dist/next/index.js";
import { aiCreditsApi, type AiCreditsOrgDto } from "../../../api/aiCreditsApi";
import { errorMessage, fmtCredits, fmtDate, parseCredits, PURCHASE_STATUS, TX_LABELS } from "./creditsUi";

type Member = AiCreditsOrgDto["members"][number];
type Usage = AiCreditsOrgDto["usageByFeature"][number];
type Tx = AiCreditsOrgDto["transactions"][number];
type Purchase = AiCreditsOrgDto["purchases"][number];

/** A personal-limit cell: saved on blur / Enter, empty = the organization's default limit. */
function LimitInput({ member, onSave }: { member: Member; onSave: (value: number | null) => Promise<void> }) {
  const [text, setText] = useState(member.monthlyLimitCredits === null ? "" : String(member.monthlyLimitCredits));
  const toast = useCkToast();
  useEffect(() => setText(member.monthlyLimitCredits === null ? "" : String(member.monthlyLimitCredits)), [member.monthlyLimitCredits]);
  const commit = () => {
    const v = parseCredits(text);
    if (v === undefined) {
      toast.error("מגבלה לא תקינה");
      return;
    }
    if (v !== member.monthlyLimitCredits) void onSave(v);
  };
  return (
    <TextInput
      ltr
      inputMode="numeric"
      value={text}
      placeholder="ברירת מחדל"
      aria-label={`מגבלה אישית — ${member.displayName}`}
      style={{ maxWidth: 120 }}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") commit();
      }}
    />
  );
}

/** «קרדיטים של AI» — the organization's wallet, who may use AI, personal limits, purchase requests (ai.credits.manage). */
export function AiCreditsPanel() {
  const toast = useCkToast();
  const [data, setData] = useState<AiCreditsOrgDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [defaultLimit, setDefaultLimit] = useState("");
  const [lowBalance, setLowBalance] = useState("");
  const [busy, setBusy] = useState(false);
  const [buyOpen, setBuyOpen] = useState(false);
  const [buyCredits, setBuyCredits] = useState("1000");
  const [buyNote, setBuyNote] = useState("");

  const load = useCallback(async () => {
    try {
      const d = await aiCreditsApi.org();
      setData(d);
      setDefaultLimit(d.wallet.defaultUserLimitCredits === null ? "" : String(d.wallet.defaultUserLimitCredits));
      setLowBalance(d.wallet.lowBalanceCredits === null ? "" : String(d.wallet.lowBalanceCredits));
      setError(null);
    } catch (e) {
      setError(errorMessage(e, "לא הצלחנו לטעון את נתוני הקרדיטים"));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <Banner tone="danger">{error}</Banner>;
  const w = data?.wallet;

  const savePolicy = async (accessMode = w?.accessMode ?? "all") => {
    const limit = parseCredits(defaultLimit);
    const low = parseCredits(lowBalance);
    if (limit === undefined || low === undefined) {
      toast.error("ערך לא תקין — מספר שלם או ריק");
      return;
    }
    setBusy(true);
    try {
      await aiCreditsApi.setPolicy({ accessMode, defaultUserLimitCredits: limit, lowBalanceCredits: low });
      toast.success("נשמר");
      await load();
    } catch (e) {
      toast.error(errorMessage(e, "השמירה נכשלה"));
    } finally {
      setBusy(false);
    }
  };

  const saveMember = async (m: Member, patch: Partial<Pick<Member, "isAllowed" | "monthlyLimitCredits">>) => {
    try {
      await aiCreditsApi.setMember(m.userId, {
        isAllowed: patch.isAllowed ?? m.isAllowed,
        monthlyLimitCredits: patch.monthlyLimitCredits !== undefined ? patch.monthlyLimitCredits : m.monthlyLimitCredits,
      });
      setData((d) => (d ? { ...d, members: d.members.map((x) => (x.userId === m.userId ? { ...x, ...patch } : x)) } : d));
    } catch (e) {
      toast.error(errorMessage(e, "השמירה נכשלה"));
    }
  };

  const requestPurchase = async () => {
    const credits = parseCredits(buyCredits);
    if (!credits) {
      toast.error("כמות לא תקינה");
      return;
    }
    setBusy(true);
    try {
      await aiCreditsApi.requestPurchase({ credits, note: buyNote.trim() || null });
      toast.success("הבקשה נשלחה לאישור");
      setBuyOpen(false);
      setBuyNote("");
      await load();
    } catch (e) {
      toast.error(errorMessage(e, "שליחת הבקשה נכשלה"));
    } finally {
      setBusy(false);
    }
  };

  const cancelPurchase = async (p: Purchase) => {
    try {
      await aiCreditsApi.cancelPurchase(p.id);
      await load();
    } catch (e) {
      toast.error(errorMessage(e, "הביטול נכשל"));
    }
  };

  const pending = data?.purchases.find((p) => p.status === "pending") ?? null;
  const usedPct = w && w.monthlyAllowanceCredits ? Math.round((w.usedCredits / w.monthlyAllowanceCredits) * 100) : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {w && !w.enabled ? <Banner tone="warning">ה-AI כבוי בארגון על ידי מנהלי המערכת.</Banner> : null}
      {w?.lowBalance ? <Banner tone="warning">היתרה נמוכה — כדאי לבקש רכישת קרדיטים.</Banner> : null}
      <StatGrid min={150}>
        <StatTile
          label="יתרה זמינה"
          icon="wallet"
          tone={w?.lowBalance ? "warning" : "primary"}
          value={w ? fmtCredits(w.remainingCredits) : undefined}
          sub="קרדיטים"
          loading={!data}
        />
        <StatTile
          label="הקצאה חודשית"
          icon="calendar"
          value={w ? fmtCredits(w.monthlyAllowanceCredits) : undefined}
          sub={usedPct !== null ? `${usedPct}% נוצל` : "מתאפסת בתחילת כל חודש"}
          loading={!data}
        />
        <StatTile label="קרדיטים שנרכשו" icon="coin" tone="success" value={w ? fmtCredits(w.purchasedCredits) : undefined} sub="לא מתאפסים" loading={!data} />
        <StatTile label="נוצל החודש" icon="sparkle" tone="violet" value={w ? fmtCredits(w.usedCredits) : undefined} loading={!data} />
      </StatGrid>

      <Panel title="מי משתמש ב-AI" icon="users">
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Segmented
            ariaLabel="הרשאת שימוש"
            items={[
              { value: "all", label: "כל מי שיש לו הרשאה" },
              { value: "selected", label: "רק משתמשים שסומנו" },
            ]}
            value={w?.accessMode ?? "all"}
            onChange={(v) => void savePolicy(v === "selected" ? "selected" : "all")}
          />
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
            <Field label="מגבלה אישית חודשית (קרדיטים)" hint="ריק = ללא מגבלה אישית">
              <TextInput ltr inputMode="numeric" value={defaultLimit} onChange={(e) => setDefaultLimit(e.target.value)} />
            </Field>
            <Field label="התראה כשהיתרה יורדת מתחת ל־" hint="ריק = בלי התראה">
              <TextInput ltr inputMode="numeric" value={lowBalance} onChange={(e) => setLowBalance(e.target.value)} />
            </Field>
            <Button variant="primary" icon="check" disabled={busy || !data} onClick={() => void savePolicy()}>
              שמירה
            </Button>
          </div>
          <SimpleTable
            ariaLabel="משתמשים וקרדיטים"
            loading={!data}
            rows={data?.members ?? []}
            rowKey={(m: Member) => m.userId}
            empty="אין משתמשים פעילים"
            columns={[
              {
                key: "displayName",
                label: "משתמש",
                render: (m: Member) => (
                  <span>
                    {m.displayName}
                    {m.roles.length ? <small style={{ color: "var(--ck-text-3)" }}> · {m.roles.join(", ")}</small> : null}
                  </span>
                ),
              },
              {
                key: "isAllowed",
                label: "מורשה",
                render: (m: Member) => (
                  <Switch checked={m.isAllowed} onChange={(v) => void saveMember(m, { isAllowed: v })} />
                ),
              },
              {
                key: "limit",
                label: "מגבלה אישית",
                render: (m: Member) => <LimitInput member={m} onSave={(v) => saveMember(m, { monthlyLimitCredits: v })} />,
              },
              { key: "usedCredits", label: "נוצל החודש", align: "end", render: (m: Member) => fmtCredits(m.usedCredits) },
            ]}
          />
          <small style={{ color: "var(--ck-text-3)" }}>מנהלי הקרדיטים תמיד מורשים ואינם מוגבלים במגבלה אישית.</small>
        </div>
      </Panel>

      <Panel
        title="רכישת קרדיטים"
        icon="coin"
        actions={
          <Button size="sm" icon="plus" disabled={Boolean(pending)} onClick={() => setBuyOpen(true)}>
            בקשת רכישה
          </Button>
        }
      >
        {pending ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <Tag tone="warning">ממתינה לאישור</Tag>
            <span>
              {fmtCredits(pending.credits)} קרדיטים · {fmtDate(pending.createdAt)}
              {pending.note ? ` · ${pending.note}` : ""}
            </span>
            <Button size="sm" variant="ghost" onClick={() => void cancelPurchase(pending)}>
              ביטול הבקשה
            </Button>
          </div>
        ) : (
          <small style={{ color: "var(--ck-text-3)" }}>קרדיטים שנרכשים נכנסים ליתרה אחרי אישור מנהלי המערכת, ואינם מתאפסים בסוף החודש.</small>
        )}
        {data && data.purchases.some((p) => p.status !== "pending") ? (
          <SimpleTable
            ariaLabel="בקשות רכישה"
            rows={data.purchases.filter((p) => p.status !== "pending")}
            rowKey={(p: Purchase) => p.id}
            columns={[
              { key: "createdAt", label: "תאריך", render: (p: Purchase) => fmtDate(p.createdAt) },
              { key: "credits", label: "קרדיטים", align: "end", render: (p: Purchase) => fmtCredits(p.credits) },
              {
                key: "status",
                label: "סטטוס",
                render: (p: Purchase) => <Tag tone={PURCHASE_STATUS[p.status]?.tone ?? "neutral"}>{PURCHASE_STATUS[p.status]?.label ?? p.status}</Tag>,
              },
              { key: "requestedByName", label: "ביקש/ה", render: (p: Purchase) => p.requestedByName ?? "—" },
            ]}
          />
        ) : null}
      </Panel>

      <Panel title="צריכה החודש לפי סוכן" icon="sparkle" collapsible defaultOpen={false}>
        <SimpleTable
          ariaLabel="צריכה לפי סוכן"
          loading={!data}
          rows={data?.usageByFeature ?? []}
          rowKey={(u: Usage) => u.feature}
          empty="עוד לא היה שימוש החודש"
          columns={[
            { key: "label", label: "סוכן" },
            { key: "requests", label: "פניות", align: "end", render: (u: Usage) => u.requests.toLocaleString("he-IL") },
            { key: "credits", label: "קרדיטים", align: "end", render: (u: Usage) => fmtCredits(u.credits) },
          ]}
        />
      </Panel>

      <Panel title="תנועות בארנק" icon="history" collapsible defaultOpen={false}>
        <SimpleTable
          ariaLabel="תנועות בארנק"
          loading={!data}
          rows={data?.transactions ?? []}
          rowKey={(t: Tx) => t.id}
          empty="אין תנועות"
          columns={[
            { key: "createdAt", label: "תאריך", render: (t: Tx) => fmtDate(t.createdAt) },
            { key: "kind", label: "סוג", render: (t: Tx) => TX_LABELS[t.kind] ?? t.kind },
            { key: "credits", label: "קרדיטים", align: "end", render: (t: Tx) => (t.kind === "allowance" ? "—" : fmtCredits(t.credits)) },
            { key: "note", label: "הערה", render: (t: Tx) => t.note ?? "" },
            { key: "actorName", label: "על ידי", render: (t: Tx) => t.actorName ?? "מערכת" },
          ]}
        />
      </Panel>

      <Dialog
        open={buyOpen}
        onClose={() => setBuyOpen(false)}
        title="בקשת רכישת קרדיטים"
        icon="coin"
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setBuyOpen(false)}>
              ביטול
            </Button>
            <Button variant="primary" icon="check" disabled={busy} onClick={() => void requestPurchase()}>
              שליחה לאישור
            </Button>
          </>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Field label="כמה קרדיטים" hint="1,000 קרדיטים ≈ $4 של שימוש במודל">
            <TextInput ltr inputMode="numeric" value={buyCredits} onChange={(e) => setBuyCredits(e.target.value)} autoFocus />
          </Field>
          <Field label="הערה למנהלי המערכת">
            <TextArea rows={2} maxLength={500} value={buyNote} onChange={(e) => setBuyNote(e.target.value)} />
          </Field>
        </div>
      </Dialog>
    </div>
  );
}
